import { z } from 'zod/v4';

import { LINE_STYLES } from '../../host-enums';

import {
    IMAGE_BORDER_RADIUS_BOUNDS,
    animationSchemaLegacy,
    buttonHorizontalPositionSchema,
    imageResizeTypeSchema,
} from '@sites/entity-core';

/**
 * `styles.maxWidth` is a PIXEL cap (the view emits `max-width: ${maxWidth}px`), not a percentage —
 * and entity-core's factory default is `100`, i.e. a freshly created image would render 100px wide.
 * The editor never leaves it there: picking a file runs `refreshImageWidth()`, which raises
 * `maxWidth` to the file's natural `imageWidth`. We can't learn a URL's natural width server-side,
 * so create falls back to this value when the model supplies neither `maxWidth` nor `imageWidth`.
 */
export const IMAGE_DEFAULT_MAX_WIDTH = 600;

/** Absurdity guard only — the host UI enforces no upper bound (same policy as indent's height). */
const MAX_WIDTH_LIMIT = 5000;

/** Shared so the create side can restate it while carrying its own missing-field message. */
export const IMAGE_URL_DESCRIPTION =
    'Image source. An absolute https:// URL is used as-is; a bare path is resolved against the ' +
    "SendPulse file-manager CDN, so only pass one if it's a real file-manager path.";

export const imageUrlSchema = z.string().min(1).describe(IMAGE_URL_DESCRIPTION);

/**
 * THE image widget data shape as AI tools accept it — single source of truth shared by
 * `create_image_widget`/`update_image_widget`, so their field surface can never drift apart (same
 * reasoning as `partial-button-item.schema.ts` and text's twin). Lives here — not in a specific app
 * or the MCP server app — because both `apps/mcp-backend`'s mutations and
 * `apps/mcp-server`'s tools need the exact same shape, and neither may import the other.
 *
 * A FLAT projection of three different levels of the real entity, deliberately not a mirror of it:
 *
 * - `url`/`alt`/`link`/`isTargetBlank`/`imageWidth`/`imageHeight` live inside `data.image`, a
 *   ~40-field file-manager pick (`selectedFileSchemaLegacy`) of which only `url` is required. Those
 *   six are the ones a model can legitimately produce; the rest is file-manager metadata (CDN
 *   paths, srcsets, overlay-caption presentation) that only a real pick can fill in.
 * - `maxWidth`/`borderRadius`/`borderWidth`/`borderStyle`/`borderColor` live on the envelope's
 *   `styles` — which for image alone is a TYPED group, not the opaque JSS bag other widgets use.
 * - everything else is plain `data`.
 *
 * Flattening across the envelope like this follows indent's precedent (its `height` is really
 * `styles.height`); each caller's mutation layer maps the flat fields back. Exposing the real
 * three-level nesting would be plumbing with no upside, and handing over the whole pick object is
 * the "one giant multi-group object" shape models have repeatedly given up on.
 */
export const partialImageWidgetDataSchema = z.object({
    // --- source: folded into `data.image` ---
    url: imageUrlSchema.optional(),
    alt: z.string().optional().describe('Alt text, used for accessibility and as the image placeholder.'),
    link: z.string().optional().describe('Make the image clickable, pointing here. Omit for a non-clickable image.'),
    isTargetBlank: z.boolean().optional().describe('Open the link in a new tab. Only meaningful with link set.'),
    imageWidth: z
        .number()
        .int()
        .positive()
        .optional()
        .describe(
            "The source image's natural pixel width, if you know it. Used to size the widget when maxWidth " +
                'is omitted, mirroring what the editor does when a file is picked.'
        ),
    imageHeight: z.number().int().positive().optional().describe("The source image's natural pixel height."),

    // --- layout: plain `data` ---
    horizontalAlign: buttonHorizontalPositionSchema
        .optional()
        .describe('Image alignment inside the column: "L" (left), "M" (center) or "R" (right).'),
    resizeType: imageResizeTypeSchema
        .optional()
        .describe(
            '"scale" (default) caps the image at maxWidth and keeps its aspect ratio. "fill" stretches it to ' +
                'the full column width and CROPS it to a square, so prefer "scale" unless a cropped banner is ' +
                'what was asked for.'
        ),
    hideOnDesktop: z.boolean().optional().describe('Hide this widget on desktop screens.'),
    hideOnTablet: z.boolean().optional().describe('Hide this widget on tablet screens.'),
    hideOnMobile: z.boolean().optional().describe('Hide this widget on mobile screens.'),
    isSynchronizeStyle: z
        .boolean()
        .optional()
        .describe('Whether tablet/mobile styling stays synchronized with the desktop styling.'),
    animation: animationSchemaLegacy
        .partial()
        .optional()
        .describe(
            'Entrance animation. Partial — only the keys you provide change, e.g. ' +
                '{ "isActive": true, "animationType": "fade" }.'
        ),

    // --- appearance: folded into the envelope's typed `styles` group ---
    maxWidth: z
        .number()
        .int()
        .min(1)
        .max(MAX_WIDTH_LIMIT)
        .optional()
        .describe(
            `Maximum rendered width in PIXELS (not a percentage). Only applies with resizeType "scale". ` +
                `Defaults to imageWidth, or ${IMAGE_DEFAULT_MAX_WIDTH} when neither is given.`
        ),
    borderRadius: z
        .number()
        .int()
        .min(IMAGE_BORDER_RADIUS_BOUNDS.min)
        .max(IMAGE_BORDER_RADIUS_BOUNDS.max)
        .optional()
        .describe('Corner rounding in pixels, applied to all four corners. Use a large value for a circle.'),
    isBorder: z.boolean().optional().describe('Whether to draw a border. Required for any border* field to show.'),
    borderWidth: z.number().min(0).optional().describe('Border width in pixels. Needs isBorder true to be visible.'),
    borderStyle: z.enum(LINE_STYLES).optional().describe('Contour style. Needs isBorder true to be visible.'),
    borderColor: z.string().optional().describe('CSS border colour. Needs isBorder true to be visible.'),
});

export type PartialImageWidgetData = z.infer<typeof partialImageWidgetDataSchema>;

/** The flat keys that belong to `data.image` rather than `data` — shared with the mutation layer. */
export const IMAGE_SOURCE_KEYS = ['url', 'alt', 'link', 'isTargetBlank', 'imageWidth', 'imageHeight'] as const;

/** The flat keys that belong to the envelope's `styles` group rather than `data`. */
export const IMAGE_STYLE_KEYS = ['maxWidth', 'borderRadius', 'borderWidth', 'borderStyle', 'borderColor'] as const;
